/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { type FC } from 'react'
import { Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import InputMask from "react-input-mask"
import * as yup from 'yup'

const HAS_PHYSICAL_DISABILITY = [
  'physical',
  'auditory',
  'speech',
  'visual',
  'mental',
  'intellectual'
] as const

export const validationSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string()
    .min(3, 'O nome deve ter no mínimo 3 caracteres')
    .max(255, 'O nome deve ter no máximo 255 caracteres')
    .nullable(),
  email: yup.string()
    .email('O e-mail deve ser válido')
    .min(3, 'O e-mail deve ter no mínimo 3 caracteres')
    .max(255, 'O e-mail deve ter no máximo 255 caracteres')
    .nullable(),
  cpf: yup.string()
    .min(3, 'O CPF deve ter no mínimo 3 caracteres')
    .max(20, 'O CPF deve ter no máximo 255 caracteres')
    .default('')
    .nullable(),
  phoneNumber: yup.string()
    .min(3, 'O telefone deve ter no mínimo 3 caracteres')
    .max(255, 'O telefone deve ter no máximo 255 caracteres')
    .nullable(),
  attachment: yup.object()
    .shape({
      url: yup.string()// .url('O anexo deve ser uma URL válida')
    })
    .nullable(),
  comment: yup.string()
    .min(3, 'O comentário deve ter no mínimo 3 caracteres')
    .max(2000, 'O comentário deve ter no máximo 255 caracteres')
    .nullable(),
  cep: yup.string()
    .min(3, 'O cep deve ter no mínimo 3 caracteres')
    .max(255, 'O cep deve ter no máximo 255 caracteres')
    .nullable(),
  street: yup.string()
    .min(3, 'A rua deve ter no mínimo 3 caracteres')
    .max(255, 'A rua deve ter no máximo 255 caracteres')
    .nullable(),
  neighborhood: yup.string()
    .min(3, 'O bairro deve ter no mínimo 3 caracteres')
    .max(255, 'O bairro deve ter no máximo 255 caracteres')
    .nullable(),
  city: yup.string()
    .min(3, 'A cidade deve ter no mínimo 3 caracteres')
    .max(255, 'A cidade deve ter no máximo 255 caracteres')
    .nullable(),
  state: yup.string()
    .min(2, 'O estado deve ter no mínimo 2 caracteres')
    .max(255, 'O estado deve ter no máximo 255 caracteres')
    .nullable(),
  fatherName: yup.string()
    .min(3, 'O nome do pai deve ter no mínimo 3 caracteres')
    .max(255, 'O nome do pai deve ter no máximo 255 caracteres')
    .nullable(),
  motherName: yup.string()
    .min(3, 'O nome da mãe deve ter no mínimo 3 caracteres')
    .max(255, 'O nome da mãe deve ter no máximo 255 caracteres')
    .nullable(),
  formerEmployee: yup.string().nullable(),
  newsletter: yup.string().nullable(),
  education: yup.string().nullable(),
  apprenticeship: yup.boolean().nullable(),
  apprenticeshipDepartment: yup.string().nullable(),
  apprenticeshipYear: yup.string().nullable(),
  gender: yup.string().nullable(),
  parentName: yup.string().nullable(),
  parentSector: yup.string().nullable(),
  indication: yup.string().nullable(),
  indicationSector: yup.string().nullable(),
  suitableForAffirmativeVacancies: yup.string()
    .min(3, 'A informação sobre vagas afirmativas deve ter no mínimo 3 caracteres')
    .max(255, 'A informação sobre vagas afirmativas deve ter no máximo 255 caracteres')
    .nullable(),
  birthDate: yup.date().nullable(),
  hasPhysicalDisability: yup.array().of(yup.string().oneOf(HAS_PHYSICAL_DISABILITY).nullable()).default([]),
  previousExperiences: yup.array().of(yup.object({
    role: yup.string().default(''),
    company: yup.string().default(''),
    duration: yup.string().default(''),
    description: yup.string().default(''),
    employmentType: yup.string().default('').oneOf(['formal', 'informal']),
  })).nullable().default([])
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormWorkWithUsSubmissionHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormWorkWithUsSubmissionHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormWorkWithUsSubmission: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  handleReset,
  values,
  errors
}) => {
  const { t }= useTranslation()

  return (
    <Row>
      <Col md={12} className='mb-3'>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Código">
                <Form.Control
                  type='text'
                  name='id'
                  value={values.id ?? ''}
                  placeholder='O id será gerado automaticamente'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Nome completo">
                <Form.Control
                  type='text'
                  name='name'
                  value={values.name ?? ''}
                  placeholder='Nome completo'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.name && <p>{errors.name}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Sexo">
                <Form.Control
                  type='text'
                  name='gender'
                  value={values.gender === 'male'? 'Masculino' : values.gender === 'female' ? 'Feminino' : values.gender ?? ''}
                  placeholder='Sexo'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.gender && <p>{errors.gender}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Data de nascimento">
                <Form.Control
                  type='date'
                  name='birthDate'
                  value={values?.birthDate ? values.birthDate?.toISOString()?.split('T')[0] : ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.birthDate && <p>{errors.birthDate}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className='mb-3'>
              <FloatingLabel label="E-mail">
                <Form.Control
                  type='text'
                  name='email'
                  value={values.email ?? ''}
                  placeholder='E-mail'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.email && <p>{errors.email}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className='mb-3'>
              <FloatingLabel label="CPF">
                <Form.Control
                  type='text'
                  name='cpf'
                  value={values.cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')?? ''}
                  placeholder='CPF'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.cpf && <p>{errors.cpf}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className='mb-3'>
              <FloatingLabel label="Telefone">
                <InputMask
                  mask="(99) 99999-9999"
                  name='phoneNumber'
                  onChange={event => {
                    const value = event.target.value.replace(/\D/g, '') // Remove non-digit characters
                    setFieldValue('phoneNumber', value)
                  }}
                  readOnly
                  onBlur={handleBlur}
                  onReset={handleReset}
                  value={values.phoneNumber || ''}
                >
                  {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                    <Form.Control
                      type='text'
                      name='phoneNumber'
                      placeholder='Telefone'
                      readOnly
                      {...inputProps as any}
                    />
                  )}
                </InputMask>
              </FloatingLabel>
              {errors.phoneNumber && <p>{errors.phoneNumber}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className='mb-3'>
              <FloatingLabel label="CEP">
                <InputMask
                  mask="99999-999"
                  name='cep'
                  onChange={event => {
                    const value = event.target.value.replace(/\D/g, '') // Remove non-digit characters
                    setFieldValue('cep', value)
                  }}
                  readOnly
                  onBlur={handleBlur}
                  onReset={handleReset}
                  value={values.cep || ''}
                >
                  {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                    <Form.Control
                      type='text'
                      name='phoneNumber'
                      placeholder='CEP'
                      readOnly
                      {...inputProps as any}
                    />
                  )}
                </InputMask>
              </FloatingLabel>
              {errors.cep && <p>{errors.cep}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className='mb-3'>
              <FloatingLabel label="Estado">
                <Form.Control
                  type='text'
                  name='state'
                  value={values.state ?? ''}
                  placeholder='Estado'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.state && <p>{errors.state}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className='mb-3'>
              <FloatingLabel label="Bairro">
                <Form.Control
                  type='text'
                  name='neighborhood'
                  value={values.neighborhood ?? ''}
                  placeholder='Bairro'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.neighborhood && <p>{errors.neighborhood}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className='mb-3'>
              <FloatingLabel label="Cidade">
                <Form.Control
                  type='text'
                  name='city'
                  value={values.city ?? ''}
                  placeholder='Cidade'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.city && <p>{errors.city}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <FloatingLabel label="Rua">
                <Form.Control
                  type='text'
                  name='street'
                  readOnly
                  value={values.street ?? ''}
                  placeholder='Rua'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FloatingLabel>
              {errors.street && <p>{errors.street}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <FloatingLabel label="Vagas afirmativas">
                <Form.Control
                  type='text'
                  name='suitableForAffirmativeVacancies'
                  value={values.suitableForAffirmativeVacancies === '' ? 'Não' : values.suitableForAffirmativeVacancies ?? ''}
                  placeholder='Vagas afirmativas'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.suitableForAffirmativeVacancies && <p>{errors.suitableForAffirmativeVacancies}</p>}
            </Form.Group>
            {values.suitableForAffirmativeVacancies === 'PCD' && (
              <Form.Group as={Col} md={12} className='mb-3'>
                <Form.Label style={{ marginRight: 10 }}>Deficiências</Form.Label>
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_physical'
                  readOnly
                  inline
                  label='Física'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('physical')}
                />
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_auditory'
                  readOnly
                  inline
                  label='Auditiva'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('auditory')}
                />
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_speech'
                  readOnly
                  inline
                  label='Fala'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('speech')}
                />
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_visual'
                  readOnly
                  inline
                  label='Visual'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('visual')}
                />
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_mental'
                  readOnly
                  inline
                  label='Mental'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('mental')}
                />
                <Form.Check
                  type='checkbox'
                  name='has_physical_disability_intellectual'
                  readOnly
                  inline
                  label='Intelectual'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onReset={handleReset}
                  checked={values.hasPhysicalDisability.includes('intellectual')}
                />
                {errors.hasPhysicalDisability && <p>{errors.hasPhysicalDisability}</p>}
              </Form.Group>
            )}
            {values.previousExperiences && values.previousExperiences.length > 0 && (
              <Form.Group as={Col} md={12} className='mb-3'>
                <Form.Label>Experiências anteriores</Form.Label>
                {values.previousExperiences.map((experience, index) => (
                  <div key={index} style={{ marginBottom: 20, padding: 10, border: '1px solid #ccc', borderRadius: 5 }}>
                    <p><strong>Cargo:</strong> {experience?.role ?? ''}</p>
                    <p><strong>Empresa:</strong> {experience?.company ?? ''}</p>
                    <p><strong>Duração:</strong> {experience?.duration ?? ''}</p>
                    <p><strong>Descrição:</strong> {experience?.description ?? ''}</p>
                    <p><strong>Tipo de emprego:</strong> {experience?.employmentType === 'formal' ? 'Formal' : 'Informal'}</p>
                  </div>
                ))}
              </Form.Group>
            )}
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label="Nome do pai">
                <Form.Control
                  type='text'
                  name='fatherName'
                  value={values.fatherName ?? ''}
                  placeholder='Nome do pai'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.fatherName && <p>{errors.fatherName}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label="Nome da mãe">
                <Form.Control
                  type='text'
                  name='motherName'
                  value={values.motherName ?? ''}
                  placeholder='Nome da mãe'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.motherName && <p>{errors.motherName}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Já trabalhou na Mundial?">
                <Form.Control
                  type='text'
                  name='formerEmployee'
                  value={values.formerEmployee === 'false' ? 'Não' : values.formerEmployee === 'true' ? 'Sim' : ''}
                  placeholder='Já trabalhou na Mundial?'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.formerEmployee && <p>{errors.formerEmployee}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Receber newsletter?">
                <Form.Control
                  type='text'
                  name='newsletter'
                  value={values.newsletter === 'false' ? 'Não' : values.newsletter === 'true' ? 'Sim' : ''}
                  placeholder='Receber newsletter?'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.newsletter && <p>{errors.newsletter}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Formação acadêmica">
                <Form.Control
                  type='text'
                  name='education'
                  value={values?.education ? t(values.education) : ''}
                  placeholder='Formação acadêmica'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.education && <p>{errors.education}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <FloatingLabel label="Jovem aprendiz?">
                <Form.Control
                  type='text'
                  name='apprenticeship'
                  value={values.apprenticeship ? t('Sim') : t('Não')}
                  placeholder='Jovem aprendiz?'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.apprenticeship && <p>{errors.apprenticeship}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <FloatingLabel label="Departamento do jovem aprendiz">
                <Form.Control
                  type='text'
                  name='apprenticeshipDepartment'
                  value={values.apprenticeshipDepartment ?? ''}
                  placeholder='Departamento do jovem aprendiz'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.apprenticeshipDepartment && <p>{errors.apprenticeshipDepartment}</p>}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <FloatingLabel label="Ano do jovem aprendiz">
                <Form.Control
                  type='text'
                  name='apprenticeshipYear'
                  value={values.apprenticeshipYear ?? ''}
                  placeholder='Ano do jovem aprendiz'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.apprenticeshipYear && <p>{errors.apprenticeshipYear}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Indicação">
                <Form.Control
                  type='text'
                  name='indication'
                  value={values.indication ?? ''}
                  placeholder='Indicação'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.indication && <p>{errors.indication}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Setor de indicação">
                <Form.Control
                  type='text'
                  name='indicationSector'
                  value={values.indicationSector ?? ''}
                  placeholder='Setor de indicação'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.indicationSector && <p>{errors.indicationSector}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Possui algum parente trabalhando na Mundial?">
                <Form.Control
                  type='text'
                  name='parentName'
                  value={values.parentName ?? ''}
                  placeholder='Possui algum parente trabalhando na Mundial?'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.parentName && <p>{errors.parentName}</p>}
            </Form.Group>
            <Form.Group as={Col} md={6} className="mb-3">
              <FloatingLabel label="Setor do parente que trabalha na Mundial">
                <Form.Control
                  type='text'
                  name='parentSector'
                  value={values.parentSector ?? ''}
                  placeholder='Setor do parente que trabalha na Mundial'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                />
              </FloatingLabel>
              {errors.parentSector && <p>{errors.parentSector}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className='mb-3'>
              <FloatingLabel label="Comentário">
                <Form.Control
                  as="textarea"
                  rows={10}
                  name='comment'
                  value={values.comment === '' ? 'Não há comentários' : values.comment ?? ''}
                  placeholder='Comentário'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly
                  style={{
                    minHeight: 200,
                    maxHeight: 400,
                    overflowY: 'auto',
                    resize: 'none'
                  }}
                />
              </FloatingLabel>
              {errors.comment && <p>{errors.comment}</p>}
            </Form.Group>
          </Row>
        </form>
      </Col>
    </Row>
  )
}

const FormWorkWithUsSubmissionWrapper: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {}
}) => (
  <Formik
    component={FormWorkWithUsSubmission}
    validateOnChange
    validateOnBlur
    validateOnMount
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormWorkWithUsSubmissionWrapper
